import React, { useState } from 'react';
import PageMeta from '../components/PageMeta';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, CheckCircle2, Star, Users, Award, Globe, TrendingUp, Target,
  Zap, BookOpen, Clock, Calendar, Phone, Mail, ChevronDown, Sparkles,
  Shield, Rocket, GraduationCap, Brain, Lightbulb, Loader2,
  Diamond, Gem, Crown, Palette, Eye, Brush
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { trackWhatsAppClick, trackFormSubmission } from '../utils/analytics';
import CertificatesSection from '../components/CertificatesSection';
import AdmissionEnrollment from '../components/AdmissionEnrollment';
import EnquireNowButton from '../components/EnquireNowButton';
import HeroPricingCard from '../components/HeroPricingCard';
import BrandLogo from '../components/BrandLogo';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import MobileCTABar from '../components/MobileCTABar';

const JewelleryDesignCoursePage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/leads`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: formData.name, email: formData.email, phone: formData.phone, message: formData.message, course: 'Jewellery Designing in Blender (2 Months)', source: 'jewellery-design-page' })
      });
      if (res.ok) { trackFormSubmission('jewellery_design_application', { course: 'Jewellery Design' }); navigate('/thank-you'); }
    } catch (err) { console.error(err); }
    setSubmitting(false);
  };

  const [payForm, setPayForm] = useState({ payName: '', payEmail: '', payPhone: '' });
  const [paySubmitting, setPaySubmitting] = useState(false);

  const handleAdmissionPayment = async (e) => {
    e.preventDefault();
    setPaySubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/payments/initiate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firstname: payForm.payName, email: payForm.payEmail, phone: payForm.payPhone, course_name: 'Jewellery Designing in Blender - Admission Fee', amount: 3000 })
      });
      if (res.ok) { const html = await res.text(); document.open(); document.write(html); document.close(); }
    } catch (err) { console.error(err); }
    setPaySubmitting(false);
  };

  const scrollToForm = () => document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' });

  const curriculum = [
    { month: 'Month 1', title: 'Modelling & Texturing', topics: ['Blender Interface & Navigation', 'Jewellery Modelling Fundamentals', 'Ring, Pendant & Bracelet Design', 'Material & Texturing for Metals & Gems'] },
    { month: 'Month 2', title: 'Rendering & Portfolio', topics: ['Photorealistic Rendering Techniques', 'Product Visualization & Staging', 'Animation Turntables & Showcase', 'Client Portfolio Creation & Freelance Setup'] }
  ];

  const careers = ['Jewellery Designer', '3D Product Visualizer', 'CAD Artist', 'Freelance Designer', 'Export Design Artist', 'Luxury Brand Designer'];
  const tools = ['Blender', 'KeyShot', 'Rhinoceros 3D', 'ZBrush', 'Adobe Photoshop', 'Substance Painter'];

  const faqs = [
    { q: 'Do I need design experience?', a: 'No. The course starts from basics and teaches you professional jewellery design from scratch in Blender.' },
    { q: 'Is this useful for traditional jewellers?', a: 'Absolutely. Traditional jewellers can learn 3D visualization to present designs to clients before manufacturing.' },
    { q: 'Can I freelance after this course?', a: 'Yes. Many students start freelancing on Fiverr and Upwork within weeks of completing the course.' },
    { q: 'What kind of projects will I create?', a: 'You will design rings, pendants, bracelets, earrings, and create a full product visualization portfolio.' },
    { q: 'Is the certificate recognized?', a: 'Yes, AI School For All certificates are recognized by industry and backed by Workshop Library\'s 20+ year legacy.' }
  ];

  const testimonials = [
    { name: 'Kavita Sharma', role: 'Freelance Jewellery Designer', quote: 'I now earn over 50K/month designing jewellery on Fiverr. This course gave me the skills I needed.' },
    { name: 'Rajesh Patel', role: 'Family Jewellery Business', quote: 'We now showcase 3D renders to clients before manufacturing. It has transformed our business.' },
    { name: 'Ananya Gupta', role: '3D Visualizer at Studio', quote: 'The Blender skills I learned here got me hired at a premium design studio within a month.' }
  ];

  return (
    <div className="min-h-screen bg-white text-slate-900 overflow-x-hidden relative">
      <PageMeta
        title="Jewellery Designing Course | Job-Assured | AI School for All"
        description="Professional jewellery design course. Industry-led training with job assurance. Enroll at AI School for All."
        path="/jewellery-designing-course"
      />
      <div className="relative z-10">
      {/* STICKY MARQUEE */}
      <div className="bg-gradient-to-r from-yellow-700 via-amber-600 to-yellow-700 py-3">
        <div className="flex items-center justify-center gap-4 px-4">
          <div className="hidden md:flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-yellow-200 animate-pulse" />
            <span className="text-sm font-bold text-white">Admissions Open for Admissions Open</span>
            <span className="text-slate-900/60">|</span>
            <span className="text-sm text-yellow-200 font-semibold">Limited Seats — 49% OFF!</span>
          </div>
          <Button onClick={scrollToForm} className="bg-white text-amber-700 hover:bg-yellow-100 px-6 py-2 text-sm font-bold rounded-full">
            Reserve Your Seat <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
      <SiteHeader />


      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-12 md:pt-16 overflow-hidden text-white">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1680055199184-5c60e894a05a?w=1920&h=1080&fit=crop&q=80" alt="Luxury Gold Jewellery" className="w-full h-full object-cover" decoding="async" fetchPriority="high" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/85 to-yellow-900/30"></div>
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, rgba(251,191,36,0.4) 1px, transparent 1px)', backgroundSize: '50px 50px' }}></div>
        </div>
        <div className="container mx-auto max-w-7xl px-6 relative z-10">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-yellow-500/20 border border-yellow-500/30 rounded-full px-4 py-2 mb-6">
              <Gem className="w-4 h-4 text-yellow-400" />
              <span className="text-sm font-bold text-yellow-300">2-Month Intensive Program</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-black mb-6 leading-tight">
              Modern Jewellery Design with<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-300">Blender & 3D Visualization</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed max-w-2xl">Learn professional jewellery modelling, photorealistic rendering, and product visualization — build a portfolio that attracts clients worldwide.</p>
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button onClick={scrollToForm} className="bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-black px-8 py-6 text-lg font-bold rounded-xl">
                Enroll Now (49% OFF) <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <EnquireNowButton courseName="Jewellery Designing in Blender" size="md" label="Enquire Now" variant="outline" />
            </div>
            <div className="flex flex-wrap gap-4">
              {['Offline + Online Hybrid', 'Industry Ready Program', 'Limited Seats'].map(t => (
                <div key={t} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2">
                  <CheckCircle2 className="w-4 h-4 text-yellow-400" /><span className="text-sm text-gray-300">{t}</span>
                </div>
              ))}
            </div>
            </div>
            <div className="lg:col-span-5">
              <HeroPricingCard
                courseName="Jewellery Designing in Blender"
                oldPrice={49000}
                newPrice={25000}
                discountLabel="49% OFF"
                badge="2-Month Industry Program"
                batchDate="New Batches Every Month"
                theme="amber"
                whatsappMessage="Hi, I want to know about the Jewellery Design Course"
              />
            </div>
          </div>
        </div>
      </section>

      {/* PREMIUM TRAINER PROFILE - 3rd Section */}
      <section className="py-24 px-6 bg-gradient-to-br from-black via-yellow-950/40 to-amber-950/30 relative overflow-hidden">
        <div className="absolute top-1/4 left-0 w-[500px] h-[500px] rounded-full bg-yellow-500/15 blur-[140px]"></div>
        <div className="absolute bottom-1/4 right-0 w-[400px] h-[400px] rounded-full bg-amber-500/15 blur-[120px]"></div>
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle, rgba(251,191,36,0.6) 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>

        <div className="container mx-auto max-w-7xl relative">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-yellow-500/20 border border-yellow-500/40 rounded-full px-5 py-2 mb-5">
              <Award className="w-4 h-4 text-yellow-400" />
              <span className="text-xs font-bold text-yellow-300 uppercase tracking-widest">Meet Your Industry Mentor</span>
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-4 leading-tight">
              Learn From a <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-amber-300 to-orange-300">Real Jewellery Designer</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">3D jewellery modelling &amp; visualization by an experienced design professional.</p>
          </div>

          <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-2 relative">
              <div className="relative group">
                <div className="absolute -inset-2 bg-gradient-to-r from-yellow-500 via-amber-400 to-orange-500 rounded-3xl blur-xl opacity-40 group-hover:opacity-70 transition-opacity"></div>
                <div className="relative rounded-3xl overflow-hidden ring-4 ring-yellow-500/40 shadow-2xl shadow-yellow-500/30">
                  <img
                    src="https://customer-assets.emergentagent.com/job_8d33ea52-c764-4e32-b8e2-de0e8040a6fa/artifacts/jyd37jw6_WhatsApp%20Image%202026-05-09%20at%2010.54.31.jpeg"
                    alt="Mr. Our Expert Trainers — Lead Trainer"
                    className="w-full aspect-[4/5] object-cover"
                    data-testid="trainer-saurabh-photo" decoding="async" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <p className="text-xs font-bold text-yellow-300 uppercase tracking-widest mb-1">Lead Trainer</p>
                    <p className="text-3xl font-black text-white">Mr. Our Expert Trainers</p>
                  </div>
                </div>
                <div className="absolute -top-4 -right-4 bg-gradient-to-br from-yellow-500 to-amber-600 text-white rounded-2xl px-4 py-3 shadow-2xl rotate-3 hidden md:block">
                  <p className="text-xs font-bold opacity-90">EXPERIENCE</p>
                  <p className="text-2xl font-black">15+ Yrs</p>
                </div>
                <div className="absolute -bottom-4 -left-4 bg-white text-black rounded-2xl px-4 py-3 shadow-2xl -rotate-3 hidden md:block">
                  <p className="text-xs font-bold opacity-70">STUDENTS</p>
                  <p className="text-2xl font-black">5,000+</p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-3 space-y-6">
              <div>
                <h3 className="text-5xl md:text-6xl font-black text-white mb-3 leading-none">Mr. Our Expert Trainers</h3>
                <p className="text-xl text-yellow-300 font-semibold">Lead Trainer — Jewellery Design, 3D Modelling &amp; Visualization</p>
              </div>

              <div className="grid grid-cols-3 gap-3">
                {[
                  { num: '15+', label: 'Years Experience' },
                  { num: '5,000+', label: 'Students Trained' },
                  { num: '300+', label: 'Brand Projects' }
                ].map((s, i) => (
                  <div key={i} className="bg-gradient-to-br from-yellow-500/10 to-amber-500/5 border border-yellow-500/30 rounded-2xl p-4 text-center backdrop-blur-sm">
                    <p className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-br from-yellow-300 to-amber-400">{s.num}</p>
                    <p className="text-xs text-gray-400 mt-1">{s.label}</p>
                  </div>
                ))}
              </div>

              <p className="text-base md:text-lg text-gray-300 leading-relaxed">
                A creative designer and 3D visualization expert with over <span className="font-bold text-yellow-300">15 years</span> in the design industry. Expert Trainer has worked with leading jewellery brands and trained hundreds of students who now design for luxury houses and freelance clients globally.
              </p>

              <div className="grid sm:grid-cols-2 gap-x-4 gap-y-3 pt-2">
                {[
                  'Blender expert for jewellery modelling',
                  'Photorealistic rendering &amp; product visualization',
                  'Commercial brand &amp; client experience',
                  'Portfolio-driven, hands-on teaching',
                  'CAD &amp; jewellery design workflows',
                  'Mentorship until your first client'
                ].map((t, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-gray-300">
                    <CheckCircle2 className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <span dangerouslySetInnerHTML={{ __html: t }} />
                  </div>
                ))}
              </div>

              <div className="border-l-4 border-yellow-500 pl-5 py-2 italic text-gray-300 bg-yellow-500/5 rounded-r-xl">
                "Great jewellery starts with great visualization. I teach you to design like a luxury brand from day one."
                <p className="not-italic text-yellow-400 font-bold text-sm mt-2">— Mr. Our Expert Trainers</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY JEWELLERY DESIGN */}
      <section className="py-24 px-6 bg-white relative">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl sm:text-5xl font-black text-center mb-4">Why Jewellery Design is <span className="text-amber-600">High Demand</span></h2>
          <p className="text-slate-600 text-center mb-16 max-w-2xl mx-auto">The customized and luxury jewellery market is booming globally.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: <TrendingUp className="w-8 h-8" />, title: 'Custom Jewellery Boom', desc: 'Personalized jewellery market growing 15% annually worldwide' },
              { icon: <Diamond className="w-8 h-8" />, title: 'CAD & 3D Demand', desc: 'Luxury brands require 3D visualization before manufacturing' },
              { icon: <Globe className="w-8 h-8" />, title: 'Export Opportunities', desc: 'India is the world\'s largest jewellery exporter — designers needed' }
            ].map((item, i) => (
              <div key={i} className="bg-slate-50 border border-slate-200 rounded-2xl p-6 hover:border-amber-400 transition-all">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center text-amber-600 mb-4">{item.icon}</div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CAREERS */}
      <section className="py-24 px-6 bg-white relative">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl sm:text-5xl font-black text-center mb-16">Career <span className="text-amber-600">Opportunities</span></h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="rounded-2xl overflow-hidden border border-slate-200">
              <img src="https://images.unsplash.com/photo-1715374033196-0ff662284a7e?w=600&h=350&fit=crop&q=80" alt="Jewellery Crafting" className="w-full h-48 object-cover" decoding="async" loading="lazy" />
              <div className="p-6 bg-slate-50">
                <h3 className="text-lg font-bold text-slate-900 mb-3">Design & Visualization</h3>
                <div className="flex flex-wrap gap-2">
                  {['Jewellery Designer', '3D Product Visualizer', 'CAD Artist'].map((r, i) => (
                    <span key={i} className="bg-amber-100 text-amber-600 text-xs font-semibold px-3 py-1.5 rounded-full border border-amber-200">{r}</span>
                  ))}
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden border border-slate-200">
              <img src="https://images.unsplash.com/photo-1626252685663-64c6bf60afb1?w=600&h=350&fit=crop&q=80" alt="Jewellery Workshop" className="w-full h-48 object-cover" decoding="async" loading="lazy" />
              <div className="p-6 bg-slate-50">
                <h3 className="text-lg font-bold text-slate-900 mb-3">Freelance & Export</h3>
                <div className="flex flex-wrap gap-2">
                  {['Freelance Designer', 'Export Design Artist', 'Luxury Brand Designer'].map((r, i) => (
                    <span key={i} className="bg-amber-100 text-amber-600 text-xs font-semibold px-3 py-1.5 rounded-full border border-amber-200">{r}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="mt-12 text-center">
            <p className="text-slate-600">Freelancers earn <span className="text-amber-600 font-bold text-2xl">&#8377;30K-1.5L/month</span> | Export designers earn <span className="text-amber-600 font-bold text-2xl">&#8377;5-10 LPA</span></p>
          </div>
        </div>
      </section>

      {/* CURRICULUM */}
      <section id="curriculum" className="py-24 px-6 bg-white relative">
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, rgba(251,191,36,0.3) 0%, transparent 40%)' }}></div>
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-4xl sm:text-5xl font-black text-center mb-4">2-Month <span className="text-amber-600">Curriculum</span></h2>
          <p className="text-slate-600 text-center mb-16">Intensive hands-on jewellery design journey</p>
          <div className="grid md:grid-cols-2 gap-6">
            {curriculum.map((m, i) => (
              <div key={i} className="bg-slate-50 border border-slate-200 rounded-2xl p-6 hover:border-amber-400 transition-all">
                <span className="text-xs font-bold uppercase tracking-wider text-amber-600 bg-amber-100 px-3 py-1 rounded-full">{m.month}</span>
                <h3 className="text-lg font-bold text-slate-900 mt-4 mb-4">{m.title}</h3>
                <ul className="space-y-2">{m.topics.map((t, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm text-slate-600"><CheckCircle2 className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />{t}</li>
                ))}</ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STUDENT SHOWCASE */}
      <section className="py-24 px-6 bg-white relative">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl sm:text-5xl font-black text-center mb-16">Student <span className="text-amber-600">Showcase</span></h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: 'Diamond Ring Render', img: 'https://images.unsplash.com/photo-1684996141204-0e5aab66d6cd?w=600&h=400&fit=crop&q=80', desc: 'Photorealistic 3D ring with gem materials and studio lighting.' },
              { title: 'Gold Bracelet Visualization', img: 'https://images.unsplash.com/photo-1720093601709-66ce9c0068a1?w=600&h=400&fit=crop&q=80', desc: 'Product visualization with elegant staging for client presentations.' },
              { title: 'Pendant Collection', img: 'https://images.unsplash.com/photo-1720189031165-b6f3cf3ff940?w=600&h=400&fit=crop&q=80', desc: 'Complete product line renders ready for e-commerce and catalogs.' }
            ].map((p, i) => (
              <div key={i} className="bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl overflow-hidden hover:border-yellow-400/40 transition-all group">
                <img src={p.img} alt={p.title} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500" decoding="async" loading="lazy" />
                <div className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{p.title}</h3>
                  <p className="text-sm text-slate-600">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STUDENT WORK SHOWCASE — Horizontal Scrolling Gallery */}
      <section className="py-24 px-0 bg-gradient-to-br from-black via-yellow-950/30 to-amber-950/30 relative overflow-hidden">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[80%] h-[2px] bg-gradient-to-r from-transparent via-yellow-500/40 to-transparent"></div>
        <div className="absolute top-1/2 right-0 w-[400px] h-[400px] bg-yellow-500/10 rounded-full blur-[140px]"></div>
        <div className="absolute top-1/2 left-0 w-[400px] h-[400px] bg-amber-500/10 rounded-full blur-[140px]"></div>

        <div className="container mx-auto max-w-7xl px-6 relative mb-12">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="inline-flex items-center gap-2 bg-yellow-500/20 border border-yellow-500/40 rounded-full px-4 py-1.5 mb-4">
                <Sparkles className="w-4 h-4 text-yellow-400" />
                <span className="text-xs font-bold text-yellow-300 uppercase tracking-widest">Designed By Our Students</span>
              </div>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight">
                Student <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-amber-300 to-orange-300">Portfolio</span>
              </h2>
              <p className="text-lg text-gray-400 mt-3 max-w-xl">Photorealistic jewellery renders, 3D models &amp; brand-ready visualization projects.</p>
            </div>
            <div className="text-sm text-yellow-300 font-semibold flex items-center gap-2">
              <span className="hidden md:inline">Scroll horizontally</span>
              <ArrowRight className="w-4 h-4 animate-pulse" />
            </div>
          </div>
        </div>

        <div data-testid="student-showcase-scroll" className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-8 px-6 md:px-12 scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
          {[
            { title: 'Luxury Diamond Rings', tag: 'PHOTOREAL', img: 'https://images.unsplash.com/photo-1611591437281-460bfbe1220a?w=700&h=500&fit=crop&q=80', desc: 'Engagement & wedding ring renders with realistic diamond shaders.' },
            { title: 'Statement Necklaces', tag: 'JEWELLERY 3D', img: 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?w=700&h=500&fit=crop&q=80', desc: 'Detailed necklace modeling with gold, platinum & gemstone materials.' },
            { title: 'Designer Earrings', tag: 'STUDIO RENDER', img: 'https://images.unsplash.com/photo-1535632787350-4e68ef0ac584?w=700&h=500&fit=crop&q=80', desc: 'High-detail earring designs ready for brand catalogues.' },
            { title: 'Gold Bracelets', tag: 'PRODUCT VIZ', img: 'https://images.unsplash.com/photo-1602173574767-37ac01994b2a?w=700&h=500&fit=crop&q=80', desc: 'Photoreal product shots with studio lighting setups.' },
            { title: 'Solitaire Renders', tag: 'CINEMATIC', img: 'https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=700&h=500&fit=crop&q=80', desc: 'Hero-shot cinematic renders used for advertising & display.' },
            { title: 'Bridal Collections', tag: 'COLLECTION', img: 'https://images.unsplash.com/photo-1620656798579-1984d9e87df7?w=700&h=500&fit=crop&q=80', desc: 'Full bridal collection sets ready to pitch to luxury brands.' },
            { title: 'Brand Pitch Decks', tag: 'PORTFOLIO', img: 'https://images.unsplash.com/photo-1573408301185-9146fe634ad0?w=700&h=500&fit=crop&q=80', desc: 'Portfolio-ready jewellery pitch decks that get clients.' }
          ].map((p, i) => (
            <div key={i} className="snap-start flex-shrink-0 w-[300px] md:w-[380px] group cursor-pointer">
              <div className="relative rounded-2xl overflow-hidden border border-yellow-500/20 hover:border-yellow-400/60 transition-all">
                <img src={p.img} alt={p.title} className="w-full h-[260px] md:h-[300px] object-cover group-hover:scale-110 transition-transform duration-700" decoding="async" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent"></div>
                <div className="absolute top-3 left-3">
                  <span className="bg-yellow-500/90 text-black text-[10px] font-black px-2.5 py-1 rounded-md tracking-widest backdrop-blur-sm">{p.tag}</span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="text-xl font-black text-white mb-2">{p.title}</h3>
                  <p className="text-sm text-gray-300 line-clamp-2">{p.desc}</p>
                </div>
              </div>
            </div>
          ))}
          <div className="flex-shrink-0 w-6"></div>
        </div>

        <div className="text-center mt-6 text-sm text-gray-500">
          <span className="inline-flex items-center gap-2"><Sparkles className="w-3 h-3 text-yellow-400" /> Swipe / drag to explore <Sparkles className="w-3 h-3 text-yellow-400" /></span>
        </div>
      </section>

      <CertificatesSection theme="yellow" courseName="Mastering Jewellery Design in Blender" />

      {/* WHY AI SCHOOL */}
      <section className="py-24 px-6 bg-white relative">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl sm:text-5xl font-black text-center mb-16">Why <span className="text-amber-600">AI School For All</span></h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: <Award />, title: "20+ Year Legacy", desc: 'Workshop Library\'s decades of training excellence.' },
              { icon: <Globe />, title: 'Global Ecosystem', desc: 'Students trained across India and internationally.' },
              { icon: <Target />, title: 'Industry Curriculum', desc: 'Curriculum built with jewellery industry professionals.' },
              { icon: <Rocket />, title: 'Real Projects', desc: 'Work on actual jewellery design briefs.' },
              { icon: <Users />, title: 'Mentorship', desc: 'Personal guidance from experienced designers.' },
              { icon: <Palette />, title: 'Portfolio Ready', desc: 'Graduate with a premium design portfolio.' }
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 bg-slate-50 border border-slate-200 rounded-xl p-5 hover:border-amber-400 transition-all">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600 flex-shrink-0">{item.icon}</div>
                <div><h3 className="font-bold text-slate-900 mb-1">{item.title}</h3><p className="text-sm text-slate-600">{item.desc}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 px-6 bg-white relative">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl sm:text-5xl font-black text-center mb-16">What Students <span className="text-amber-600">Say</span></h2>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
                <div className="flex gap-1 mb-4">{[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-yellow-400 text-amber-600" />)}</div>
                <p className="text-slate-700 mb-4 italic">"{t.quote}"</p>
                <div><p className="font-bold text-slate-900">{t.name}</p><p className="text-sm text-amber-600">{t.role}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CERT + TOOLS */}
      <section className="py-24 px-6 bg-white relative">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-black mb-6">Industry <span className="text-amber-600">Certification</span></h2>
              <div className="bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl p-8">
                <Award className="w-12 h-12 text-amber-600 mb-4" />
                <h3 className="text-xl font-bold text-slate-900 mb-2">AI School For All Certificate</h3>
                <p className="text-slate-600 mb-4">Powered by Workshop Library — recognized by design studios and jewellery brands.</p>
                <ul className="space-y-2">
                  {['Industry-recognized credential', 'Premium design portfolio included', 'Freelance setup assistance', 'Export industry connections'].map((t, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle2 className="w-4 h-4 text-amber-600" />{t}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div>
              <h2 className="text-3xl font-black mb-6">Tools You'll <span className="text-amber-600">Master</span></h2>
              <div className="grid grid-cols-2 gap-3">
                {tools.map((t, i) => (
                  <div key={i} className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center hover:border-amber-400 transition-all">
                    <p className="text-sm font-semibold text-slate-900">{t}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 bg-white relative">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-4xl sm:text-5xl font-black text-center mb-16">Frequently Asked <span className="text-amber-600">Questions</span></h2>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left">
                  <span className="font-semibold text-slate-900 pr-4">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-amber-600 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && <div className="px-5 pb-5 text-slate-600 text-sm">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* DETAILED ENROLLMENT + ADMISSION PAYMENT */}
      <AdmissionEnrollment courseName="Jewellery Designing in Blender" theme="yellow" source="yellow-course-page" />
      <MobileCTABar courseName="Jewellery Designing" />

<SiteFooter accent="amber" />

      {/* WHATSAPP */}
      <a href="https://wa.me/919891550666?text=Hi, I want to know about the Jewellery Design Course" target="_blank" rel="noopener noreferrer"
        onClick={() => trackWhatsAppClick('jewellery_page')} className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-2xl transition-all hover:scale-110" data-testid="whatsapp-float">
        <Phone className="w-6 h-6" />
      </a>

      <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white/95 backdrop-blur-md border-t border-amber-200 shadow-2xl p-3">
        <Button onClick={scrollToForm} className="w-full bg-gradient-to-r from-yellow-500 to-amber-500 text-black py-3 font-bold rounded-xl">
          Enroll Now — &#8377;25,000 (49% OFF)
        </Button>
      </div>
      </div>
    </div>
  );
};

export default JewelleryDesignCoursePage;
